import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { cloneDeep, get, some, isEqual, } from 'lodash'
import { Box, Container, Typography } from '@material-ui/core'
import { makeStyles, useTheme } from '@material-ui/core/styles'

import useTitle from 'common/use-title'
import useScid from 'common/use-scid'
import useIsShadow from 'common/use-is-shadow'
import api from 'common/api'
import { actions } from 'store/snackbar'
import { useAppCycle, useRoundConfigurationAppCycle } from 'store/application-cycle/hooks'

import ApplicationTeaser from '../ApplicationTeaser'
import Banner from './components/RoleBanner'
import UnsavedPrompt from './components/UnsavedPrompt'
import Header from './Header'
import GeneralSection from './GeneralSection'
import ProfileSection from './ProfileSection'
import FeeWaivers, { FEE_WAIVER_KEYS } from './FeeWaivers'
import TestSection from './TestSection'
import SupplementalSection from './SupplementalSection'
import NotesSection from './NotesSection'
import SupplementalEssay from './SupplementalEssay'
import EPTestGuidelines from './EPTestGuidelines'
import AdditionalExams from './AdditionalExams'
import OtherDetails from './OtherDetails'
import Footer from './Footer'
import useCanConfigureApplications, {
  useCanViewReadOnly,
  useIsApplicationIntegrationEnabled,
} from '../useCanConfigureApplications'
import ContactSection from './ContactSection'
import LockedBanner from './components/LockedBanner'
import { useConfigureUpcomingApplicationCycle } from 'store/feature-toggle/hooks'

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: get(theme, 'palette.pageBackground.main'),
  },
  componentWrapper: {
    backgroundColor: get(theme, 'palette.pageBackground.main'),
    paddingBottom: theme.spacing(10),
  },
  pageTitle: {
    marginBottom: theme.spacing(2),
  },
  sectionBottom: {
    marginBottom: theme.spacing(4),
  },
  sectionDividerBottom: {
    marginBottom: theme.spacing(5.5),
  },
  subHeading: {
    fontWeight: 500,
    marginBottom: theme.spacing(1),
  },
  thirdLevelHeading: {
    color: theme.palette.text.secondary,
  },
  link: {
    fontWeight: 'bold',
  },
  requiredWarning: {
    textAlign: 'right',
  },
}))

export const ApplicationSettings = () => {
  useTitle('Scoir - Configure Application')
  const classes = useStyles()
  const theme = useTheme()
  const dispatch = useDispatch()


  const isConfigureUpcomingEnabled = useConfigureUpcomingApplicationCycle()
  const currentAppCycle = useAppCycle()
  const upcomingCycle = useRoundConfigurationAppCycle()
  const [appCycle, setAppCycle] = useState(isConfigureUpcomingEnabled ? upcomingCycle : currentAppCycle)
  const [isCurrentAppCycle, setIsCurrentAppCycle] = useState(!isConfigureUpcomingEnabled)
  const [currentAppConfig, setCurrentAppConfig] = useState({})
  const [upcomingAppConfig, setUpcomingAppConfig] = useState({})

  const [upcomingLoading, setUpcomingLoading] = useState(true)
  const [currentLoading, setCurrentLoading] = useState(true)

  const loading = upcomingLoading || currentLoading
  const canToggle = isConfigureUpcomingEnabled && !!upcomingAppConfig?.id && !!currentAppConfig?.id 

  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [formErrors, setFormErrors] = useState({})
  const [focusedAppConfig, setFocusedAppConfig] = useState(null)

  const updateApplicationConfig = useCallback(
    (updates) => {
      setFocusedAppConfig({
        ...focusedAppConfig,
        ...updates,
      })
    },
    [focusedAppConfig],
  )

  const showMissingFeeWaivers = useMemo(
    () => !!FEE_WAIVER_KEYS.find((key) => get(focusedAppConfig, `feeWaivers.${key}`, '') === ''),
    [focusedAppConfig],
  )

  const [persistedApplicationConfig, setPersistedApplicationConfig] = useState({})
  const scid = useScid()
  const isApplicationIntegrationEnabled = useIsApplicationIntegrationEnabled()
  const showTeaser = !isApplicationIntegrationEnabled
  const canConfigureApplications = useCanConfigureApplications()
  const canViewReadOnly = useCanViewReadOnly()
  const [showWarnings, setShowWarnings] = useState(false)
  const [isActivated, setIsActivated] = useState(false)
  const isShadow = useIsShadow()
  
  const getCurrentAppConfig = useCallback(() => {
    api
      .get(`/api/colleges/${scid}/integrations/application/configuration/${currentAppCycle}`)
      .then((response) => {
        setCurrentAppConfig(response)
      })
      .catch(() => {
        dispatch(actions.addSnackbarItem('An error ocurred loading Application Settings!'))
      }).finally(() => {
        setCurrentLoading(false)
      })
  }, [scid, currentAppCycle, dispatch])

  const getUpcomingAppConfig = useCallback(() => {
    if (!isConfigureUpcomingEnabled){
      setUpcomingLoading(false)
      return
    }

    api
      .get(`/api/colleges/${scid}/integrations/application/configuration/${upcomingCycle}`)
      .then((response) => {
        setUpcomingAppConfig(response)
      })
      .catch(() => {
        dispatch(actions.addSnackbarItem('An error ocurred loading Application Settings!'))
      }).finally(() => {
        setUpcomingLoading(false)
      })
  }, [dispatch, isConfigureUpcomingEnabled, scid, upcomingCycle])

  useEffect(() => {
    const focused = isCurrentAppCycle ? currentAppConfig : upcomingAppConfig
    setPersistedApplicationConfig(cloneDeep(focused))
    const verificationActivated = focused.verification ? focused.verification.activated : false
    setIsActivated(verificationActivated && focused.scoirApplyEnabled && !isShadow)
    setFocusedAppConfig(focused)
  }, [isCurrentAppCycle, currentAppConfig, upcomingAppConfig, isShadow])

  useEffect(() => {
    getUpcomingAppConfig()
    getCurrentAppConfig()
  }, [getCurrentAppConfig, getUpcomingAppConfig])

  const showTestingPolicyWarning = useMemo(() => {
    const fyTestPolicy = focusedAppConfig?.firstYearConfiguration?.testingPolicy?.requirements
    const fyEnabled = focusedAppConfig?.firstYearConfiguration?.enabled

    const trTestPolicy = focusedAppConfig?.transferConfiguration?.testingPolicy?.requirements
    const trEnabled = focusedAppConfig?.transferConfiguration?.enabled

    return (fyEnabled && fyTestPolicy == null) || (trEnabled && trTestPolicy == null)
  }, [focusedAppConfig])

  const handleSave = useCallback(() => {
    setShowWarnings(true)
    if (showMissingFeeWaivers || showTestingPolicyWarning) {
      return
    }

    setIsSaving(true)
    api
      .put(`/api/colleges/${scid}/integrations/application/configuration/${appCycle}/settings`, focusedAppConfig)
      .then(() => {
        setIsEditing(false)
        setIsSaving(false)
        setPersistedApplicationConfig(cloneDeep(focusedAppConfig))
        dispatch(actions.addSnackbarItem('Application Settings saved successfully!'))
      })
      .catch(() => {
        dispatch(actions.addSnackbarItem('An error ocurred saving Application Settings!'))
        setIsSaving(false)
      })
  }, [showTestingPolicyWarning, showMissingFeeWaivers, appCycle, focusedAppConfig, dispatch, scid])


  const onIsEditingToggle = (reqState) => {
    setIsEditing(reqState)
    setShowWarnings(false)

    if (!reqState) {
      setFocusedAppConfig(cloneDeep(persistedApplicationConfig))
    }
  }

  const isDirtyFunc = () => {
    return !isEqual(focusedAppConfig, persistedApplicationConfig)
  }

  const isLocked = () => {
    return isEditing && isActivated
  }

  const getStyle = (baseStyle = null) => {
    return isLocked() ? theme.palette.text.secondary : baseStyle ? baseStyle : theme.palette.text.primary
  }

  return (
    <React.Fragment>
      <div className={classes.root}>
        {loading && <span></span>}
        {!loading && (!showTeaser || canViewReadOnly) && (
          <Box>
            <Box
              className={classes.componentWrapper}
              px={[0, 3, 5]}
              py={5}
            >
              <Container
                component="form"
                maxWidth="lg"
                autoComplete="on"
              >
                <UnsavedPrompt
                  hasChanges={isEditing}
                  isDirtyFunc={isDirtyFunc}
                />
                <Banner />
                <LockedBanner
                  isEditing={isEditing}
                  isActivated={isActivated}
                />
                <Box className={classes.pageTitle}>
                  <Header
                    canEdit={canConfigureApplications}
                    onSave={handleSave}
                    isEditing={isEditing}
                    onIsEditingToggle={onIsEditingToggle}
                    isSaving={isSaving}
                    isError={some(formErrors, (v) => v)}
                    setFormErrors={setFormErrors}
                    loading={loading}
                    canChangeAppCycle={canToggle}
                    appCycle={appCycle}
                    isCurrentAppCycle={isCurrentAppCycle}
                    onChangeAppCycle={(isCurrent) => {
                      setIsCurrentAppCycle(isCurrent)
                      setAppCycle(isCurrent ? currentAppCycle : upcomingCycle)
                      setFocusedAppConfig(isCurrent ? currentAppConfig : upcomingAppConfig)
                    }}
                  />
                  {showWarnings && showTestingPolicyWarning && (
                    <Box
                      mb={4}
                      className={classes.requiredWarning}
                    >
                      <Typography
                        variant="caption"
                        color="error"
                      >
                        Missing Section: Standardized Test Policy
                      </Typography>
                    </Box>
                  )}
                  {showWarnings && showMissingFeeWaivers && (
                    <Box
                      mb={4}
                      className={classes.requiredWarning}
                    >
                      <Typography
                        variant="caption"
                        color="error"
                      >
                        Missing Section: Fee Waivers
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Box className={classes.sectionDividerBottom}>
                  <GeneralSection
                    isEditing={isEditing && !isActivated}
                    applicationConfig={focusedAppConfig}
                    setApplicationConfig={(cfg, isError) => {
                      updateApplicationConfig(cfg)
                      setFormErrors({
                        ...formErrors,
                        general: isError,
                      })
                    }}
                    loading={loading}
                    getStyle={getStyle}
                  />
                </Box>
                <Box className={classes.sectionBottom}>
                  <Box mb={4}>
                    <Typography
                      style={{ color: getStyle() }}
                      variant="h5"
                      component="h2"
                      className={classes.subHeading}
                    >
                      Application Profile
                    </Typography>
                    <Typography className={classes.thirdLevelHeading}>
                      Choose the profile sections you wish to collect from your applicants. View our&nbsp;
                      <a
                        className={classes.link}
                        href="https://hubs.ly/Q01btjq10"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Application Profile Specification Guide
                      </a>
                      &nbsp;for more details.
                    </Typography>
                  </Box>

                  <ProfileSection
                    isEditing={isEditing && !isActivated}
                    applicationConfig={focusedAppConfig}
                    setApplicationConfig={updateApplicationConfig}
                    loading={loading}
                    getStyle={getStyle}
                  />
                </Box>
                <Box className={classes.sectionBottom}>
                  <TestSection
                    isEditing={isEditing && !isActivated}
                    applicationConfig={focusedAppConfig}
                    setApplicationConfig={(cfg, isError) => {
                      setFocusedAppConfig(cfg)
                      setFormErrors({
                        ...formErrors,
                        test: isError,
                      })
                    }}
                    loading={loading}
                    showRequiredWarning={showWarnings && showTestingPolicyWarning}
                    getStyle={getStyle}
                  />
                </Box>
                <Box className={classes.sectionBottom}>
                  <AdditionalExams
                    isEditing={isEditing}
                    getStyle={getStyle}
                    applicationConfig={focusedAppConfig}
                    setApplicationConfig={(cfg, isError) => {
                      setFocusedAppConfig(cfg)
                      setFormErrors({
                        ...formErrors,
                        additionalExams: isError,
                      })
                    }}
                    loading={loading}
                  />
                </Box>
                <Box className={classes.sectionBottom}>
                  <EPTestGuidelines
                    isEditing={isEditing}
                    getStyle={getStyle}
                    applicationConfig={focusedAppConfig}
                    setApplicationConfig={(cfg, isError) => {
                      setFocusedAppConfig(cfg)
                      setFormErrors({
                        ...formErrors,
                        epTest: isError,
                      })
                    }}
                    loading={loading}
                  />
                </Box>
                <Box className={classes.sectionDividerBottom}>
                  <OtherDetails
                    isEditing={isEditing}
                    applicationConfig={focusedAppConfig}
                    setApplicationConfig={updateApplicationConfig}
                    getStyle={getStyle}
                    loading={loading}
                  />
                </Box>
                <Box className={classes.sectionBottom}>
                  <>
                    <Box mb={4}>
                      <Typography
                        variant="h5"
                        component="h2"
                        className={classes.subHeading}
                      >
                        Fee Waiver Details
                      </Typography>
                      <Typography className={classes.thirdLevelHeading}>
                        Choose the application fee-waiver criteria that will be supported by your institution.
                      </Typography>
                    </Box>
                    <FeeWaivers
                      isEditing={isEditing && !isActivated}
                      applicationConfig={focusedAppConfig}
                      updateApplicationConfig={updateApplicationConfig}
                      loading={loading}
                      getStyle={getStyle}
                      showWarnings={showWarnings}
                    />
                  </>
                </Box>
                <Box className={classes.sectionDividerBottom}>
                  <Box mb={4}>
                    <Typography
                      variant="h5"
                      component="h2"
                      className={classes.subHeading}
                    >
                      Supplement Details
                    </Typography>
                    <Typography className={classes.thirdLevelHeading}>
                      Help students understand how many additional questions they will need to answer after completing
                      their application in Scoir.
                    </Typography>
                  </Box>

                  <Box className={classes.sectionBottom}>
                    <SupplementalSection
                      isEditing={isEditing}
                      applicationConfig={focusedAppConfig}
                      setApplicationConfig={(cfg, isError) => {
                        updateApplicationConfig(cfg)
                        setFormErrors({
                          ...formErrors,
                          supplemental: isError,
                        })
                      }}
                      loading={loading}
                    />
                  </Box>
                  <Box className={classes.sectionBottom}>
                    <SupplementalEssay
                      isEditing={isEditing}
                      applicationConfig={focusedAppConfig}
                      setApplicationConfig={(cfg, isError) => {
                        updateApplicationConfig(cfg)
                        setFormErrors({
                          ...formErrors,
                          supplementalEssay: isError,
                        })
                      }}
                      loading={loading}
                    />
                  </Box>
                  <Box>
                    <NotesSection
                      isEditing={isEditing}
                      applicationConfig={focusedAppConfig}
                      setApplicationConfig={(cfg, isError) => {
                        updateApplicationConfig(cfg)
                        setFormErrors({
                          ...formErrors,
                          notes: isError,
                        })
                      }}
                      loading={loading}
                    />
                  </Box>
                </Box>
                <Box className={classes.sectionBottom}>
                  <Box mb={4}>
                    <Typography
                      variant="h5"
                      component="h2"
                      className={classes.subHeading}
                    >
                      Admissions Contact
                    </Typography>
                    <Typography className={classes.thirdLevelHeading}>
                      This info will be used by Scoir Support to contact your institution with any questions, either
                      from Scoir or potential applicants, about the application.
                    </Typography>
                  </Box>

                  <ContactSection
                    isEditing={isEditing}
                    applicationConfig={focusedAppConfig}
                    setApplicationConfig={(cfg, isError) => {
                      updateApplicationConfig(cfg)
                      setFormErrors({
                        ...formErrors,
                        contact: isError,
                      })
                    }}
                    loading={loading}
                  />
                </Box>
              </Container>
            </Box>
            <Footer />
          </Box>
        )}
        {!loading && showTeaser && <ApplicationTeaser />}
      </div>
    </React.Fragment>
  )
}

export default ApplicationSettings
